import React, { useId, useState } from 'react';
import { CustomIcon } from '../CustomIcon/CustomIcon';
import { VideoModal } from '../VideoModal/VideoModal';
import { DownloadIcon, PlayCircleIcon } from '../../utils/inlineIcons';
import { ctaTargetAttributes, joinDescribedBy, CtaBehavior, CTA_NEW_TAB_DESCRIPTION } from '../../utils/ctaBehavior';
import type { CtaAction } from '../../utils/ctaAction';
import { dispatchPopupEvent, normalizePopupId } from '../../utils/popupEvents';
import { CTA_PREFIXES, useClickTracking } from '../../hooks/useClickTracking';
import './ArrowLink.scss';

/**
 * ArrowLink - Shared text link with animated arrow
 *
 * Uses a semantic anchor for navigation actions and button semantics for Popup actions, with the
 * same text-link styling and animated arrow in either case. Used across ContentGrid, CardGrid,
 * FeaturedCards, and other components for "Learn more" style CTAs.
 *
 * No Cloudscape dependency — just semantic HTML with consistent
 * typography and animation from design tokens. The arrow icon comes from
 * the shared CustomIcon registry (`arrow-forward`) so the entire codebase
 * uses one canonical right-arrow shape.
 */

export type ArrowDirection = 'right' | 'diagonal';

export interface ArrowLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  /** Arrow direction: 'right' (→) or 'diagonal' (↗ via CSS background-image) */
  direction?: ArrowDirection;
  /**
   * Whether the link opens in the current tab or a new one; new tab adds `rel="noopener noreferrer"`
   * and a screen-reader-only "Opens in a new tab" description.
   */
  behavior?: CtaBehavior;
  /**
   * What the link does when activated (default `link`). `video` replaces the arrow with a play icon
   * and opens `href` in a video modal; `download` replaces the arrow with a download icon; `popup`
   * keeps the arrow and opens the Popup block matching `popupTargetId` without following `href`.
   */
  action?: CtaAction;
  /**
   * Popup ID to open when `action` is `popup`.
   * @polyTranslatable false
   */
  popupTargetId?: string;
  /** Called when the `video` action opens its modal. */
  onVideoOpen?: () => void;
  /** Called when the `video` action's modal closes. */
  onVideoClose?: () => void;
  /**
   * Analytics CTA identifier. Falls back to an `arw_`-prefixed slug derived from `href`.
   * @polyTranslatable false
   */
  ctaId?: string;
}

const ACTION_ICONS = { video: PlayCircleIcon, download: DownloadIcon } as const;

export const ArrowLink: React.FC<ArrowLinkProps> = ({
  direction = 'right',
  behavior,
  action = 'link',
  popupTargetId,
  children,
  className,
  onClick,
  onKeyDown,
  onVideoOpen,
  onVideoClose,
  'aria-describedby': ariaDescribedBy,
  ctaId,
  ...anchorProps
}) => {
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const generatedId = useId();
  const opensPopup = action === 'popup';
  const newTabDescriptionId = behavior === 'newTab' && !opensPopup ? `${generatedId}-new-tab` : undefined;
  const opensVideo = action === 'video' && !!anchorProps.href;
  const ActionIcon = action === 'video' || action === 'download' ? ACTION_ICONS[action] : null;
  const dirClass = ActionIcon ? 'arrow-link--action' : direction === 'diagonal' ? 'arrow-link--diagonal' : 'arrow-link--right';
  const classes = ['arrow-link', opensPopup && 'arrow-link--button', dirClass, className].filter(Boolean).join(' ');
  const resolvedHref = opensPopup ? undefined : anchorProps.href;

  const trackClick = useClickTracking(CTA_PREFIXES.arrowLink, {
    ctaId,
    href: resolvedHref,
    label: opensPopup ? normalizePopupId(popupTargetId) : undefined,
  });

  const handleClick: React.MouseEventHandler<HTMLAnchorElement> = (event) => {
    trackClick();
    onClick?.(event);
    if (event.defaultPrevented) return;
    if (opensPopup) {
      event.preventDefault();
      dispatchPopupEvent(event.currentTarget, popupTargetId ?? '');
    } else if (opensVideo) {
      event.preventDefault();
      setIsVideoOpen(true);
      onVideoOpen?.();
    }
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLAnchorElement> = (event) => {
    onKeyDown?.(event);
    if (event.defaultPrevented) return;
    if (opensPopup && (event.key === ' ' || event.key === 'Enter')) {
      event.preventDefault();
      event.currentTarget.click();
    }
  };

  const closeVideo = () => {
    setIsVideoOpen(false);
    onVideoClose?.();
  };

  return (
    <>
      <a
        className={classes}
        data-cta-id={ctaId || undefined}
        {...anchorProps}
        href={resolvedHref}
        {...ctaTargetAttributes(opensPopup ? undefined : behavior)}
        {...(action === 'download' && { download: true })}
        role={opensPopup ? 'button' : undefined}
        tabIndex={opensPopup ? 0 : anchorProps.tabIndex}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        aria-describedby={joinDescribedBy(ariaDescribedBy, newTabDescriptionId)}
      >
        {children}
        {ActionIcon && (
          <ActionIcon className="arrow-link__arrow arrow-link__action-icon" />
        )}
        {!ActionIcon && direction === 'right' && (
          <span className="arrow-link__arrow" aria-hidden="true">
            <CustomIcon name="arrow-forward" size="1em" aria-hidden="true" />
          </span>
        )}
        {!ActionIcon && direction === 'diagonal' && (
          <span className="arrow-link__arrow arrow-link__arrow--diagonal" aria-hidden="true" />
        )}
      </a>
      {newTabDescriptionId && (
        <span id={newTabDescriptionId} className="poly-visually-hidden">{CTA_NEW_TAB_DESCRIPTION}</span>
      )}
      {isVideoOpen && anchorProps.href && (
        <VideoModal src={anchorProps.href} onClose={closeVideo} />
      )}
    </>
  );
};
